import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Check, X } from '@phosphor-icons/react';
import { format } from 'date-fns';
import api from '../../utils/api';
import { filterLayoutBlocks } from '../../utils/proposalCraftUtils';
import { getLineItemTotal, getUnitPriceWithTax } from '../../utils/taxCalculations';
import { renderProposalBlock } from './proposalBlockView';
import AgreementTermsViewer from './AgreementTermsViewer';
import AgreementAcceptedAdditionalInfo from './AgreementAcceptedAdditionalInfo';

/** Stable key for pricing rows; avoids duplicate React keys when _id is missing. */
function getProposalLineItemKey(item, index) {
  if (item?._id != null && item._id !== '') return String(item._id);
  return `line-${index}`;
}

function ProposalViewPage() {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOptionals, setSelectedOptionals] = useState({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [additionalInfoValues, setAdditionalInfoValues] = useState({});

  const additionalFields = proposal?.additionalInformationFields || [];
  const isAccepted = proposal?.status === 'accepted';
  const isBlockedByParent =
    proposal?.agreementType === 'addendum' &&
    proposal?.parentAgreement &&
    proposal?.parentAgreementStatus !== 'accepted';
  const fetchProposal = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get(`/api/agreements/view/${token}`);
      setProposal(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Agreement not found or no longer available');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchProposal();
  }, [token, fetchProposal]);

  useEffect(() => {
    if (!proposal?.additionalInformationFields?.length) {
      setAdditionalInfoValues({});
      return;
    }
    const initial = {};
    for (const field of proposal.additionalInformationFields) {
      initial[field.name] = proposal.existingAdditionalInformation?.[field.name] || '';
    }
    setAdditionalInfoValues(initial);
  }, [proposal?._id, proposal?.additionalInformationFields, proposal?.existingAdditionalInformation]);

  const additionalInfoComplete = useMemo(() => {
    if (!additionalFields.length) return true;
    return additionalFields.every((field) => String(additionalInfoValues[field.name] || '').trim() !== '');
  }, [additionalFields, additionalInfoValues]);

  const getQty = (item) => item.quantity ?? 1;

  /** Optional extras are opt-in: unchecked by default; the couple ticks to include them in the total and acceptance. */
  const isOptionalLineIncluded = (item, index) => {
    if (!item.isOptional) return true;
    const key = getProposalLineItemKey(item, index);
    const v = selectedOptionals[key];
    if (v !== undefined) return v;
    return false;
  };

  const getTotal = () => {
    const items = isAccepted ? visibleLineItems : (proposal?.lineItems || []);
    if (items.length === 0) return 0;
    let total = 0;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!isAccepted && item.isOptional && !isOptionalLineIncluded(item, i)) continue;
      total += getLineItemTotal(item);
    }
    return Math.round(total * 100) / 100;
  };

  const getSelectedOptionalIds = () =>
    proposal?.lineItems
      ?.map((item, idx) => ({ item, idx }))
      ?.filter(
        ({ item, idx }) =>
          item.isOptional && isOptionalLineIncluded(item, idx) && item._id != null && String(item._id) !== 'undefined'
      )
      ?.map(({ item }) => item._id) || [];

  const handleAccept = async () => {
    if (!agreedToTerms) {
      setError('You must agree to the terms to accept');
      return;
    }
    if (!signerName.trim()) {
      setError('Please enter your full name');
      return;
    }
    if (isBlockedByParent) {
      setError('The original agreement must be accepted before this addendum can be signed');
      return;
    }
    if (!additionalInfoComplete) {
      setError('Please complete all additional information fields');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/api/agreements/accept', {
        token,
        selectedOptionalIds: getSelectedOptionalIds(),
        agreedToTerms: true,
        signerName: signerName.trim(),
        additionalInformationResponses: additionalFields.map((field) => ({
          name: field.name,
          value: String(additionalInfoValues[field.name] || '').trim()
        }))
      });
      setSuccess('accepted');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to accept agreement';
      const detail = err.response?.data?.detail;
      setError(detail ? `${msg}: ${detail}` : msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    setSubmitting(true);
    setError('');
    try {
      await api.post('/api/agreements/decline', {
        token,
        reason: declineReason
      });
      setSuccess('declined');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to decline agreement');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency
    }).format(amount);
  };

  const visibleLineItems = useMemo(() => {
    const items = proposal?.lineItems || [];
    if (!isAccepted) return items;
    const selectedIds = new Set(
      (proposal?.acceptance?.selectedOptionalIds || []).map(String)
    );
    return items.filter(
      (item) => !item.isOptional || selectedIds.has(String(item._id))
    );
  }, [proposal?.lineItems, isAccepted, proposal?.acceptance?.selectedOptionalIds]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !proposal) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ p: 3, maxWidth: 500, mx: 'auto', textAlign: 'center' }}>
        <Paper sx={{ p: 4 }}>
          <Check size={48} weight="bold" style={{ color: success === 'accepted' ? '#2e7d32' : '#757575', marginBottom: 16 }} />
          <Typography variant="h5" gutterBottom>
            {success === 'accepted' ? 'Agreement accepted' : 'Agreement declined'}
          </Typography>
          <Typography color="text.secondary">
            {success === 'accepted'
              ? 'Thank you. Your supplier will be in touch with next steps.'
              : 'You have declined this agreement.'}
          </Typography>
        </Paper>
      </Box>
    );
  }

  const businessName = proposal?.business?.name || 'Your supplier';
  const blocks = proposal?.content?.blocks || [];
  const layoutBlocks = filterLayoutBlocks(blocks);
  const legacyFreeform = layoutBlocks.length === 0 && blocks.length === 0;

  const renderPricingContent = () => (
    <>
      <Box component="ul" sx={{ listStyle: 'none', pl: 0, m: 0 }}>
        {visibleLineItems.map((item, rowIndex) => {
          const qty = getQty(item);
          const unitWithTax = getUnitPriceWithTax(item);
          const lineKey = getProposalLineItemKey(item, rowIndex);
          const lineTotal = getLineItemTotal(item);
          return (
            <Box
              key={lineKey}
              component="li"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
                {!isAccepted && item.isOptional && (
                  <Checkbox
                    checked={isOptionalLineIncluded(item, rowIndex)}
                    onChange={() => {
                      const key = getProposalLineItemKey(item, rowIndex);
                      const current = isOptionalLineIncluded(item, rowIndex);
                      setSelectedOptionals((prev) => ({ ...prev, [key]: !current }));
                    }}
                    size="small"
                  />
                )}
                <Typography variant="body2" component="span">
                  {item.description}
                  {item.isOptional && !isAccepted && ' (optional)'}
                </Typography>
                <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                  {qty}× {formatCurrency(unitWithTax, proposal.currency)}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={500}>
                {formatCurrency(lineTotal, proposal.currency)}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6">Total: {formatCurrency(getTotal(), proposal?.currency)}</Typography>
      </Box>
    </>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 720, mx: 'auto' }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Agreement from {businessName}
        </Typography>
        {proposal?.updatedAfterSendAt && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Updated {format(new Date(proposal.updatedAfterSendAt), 'd MMMM yyyy HH:mm')} (UK)
          </Typography>
        )}

        {layoutBlocks.length > 0 && (
          <>
            {layoutBlocks.map((block, idx) => renderProposalBlock(block, `root-${idx}`))}
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {legacyFreeform && proposal?.message && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {proposal.message}
            </Typography>
          </Box>
        )}

        {legacyFreeform && proposal?.message && <Divider sx={{ my: 2 }} />}

        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Services & pricing
        </Typography>
        {renderPricingContent()}

        {proposal?.termsText && (
          <>
            <Divider sx={{ my: 2 }} />
            <AgreementTermsViewer termsText={proposal.termsText} />
          </>
        )}

        {additionalFields.length > 0 && !isAccepted && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Additional information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 1 }}>
              {additionalFields.map((field) => (
                <TextField
                  key={field.name}
                  fullWidth
                  label={field.label}
                  placeholder={field.placeholder || ''}
                  value={additionalInfoValues[field.name] || ''}
                  onChange={(e) =>
                    setAdditionalInfoValues((prev) => ({ ...prev, [field.name]: e.target.value }))
                  }
                  required
                  multiline={field.type === 'textarea'}
                  minRows={field.type === 'textarea' ? 3 : undefined}
                />
              ))}
            </Box>
          </>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!isAccepted && !showDeclineForm && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
            {isBlockedByParent && (
              <Alert severity="info">
                This addendum will be available to sign once the original agreement has been accepted.
              </Alert>
            )}
            <FormControlLabel
              control={<Checkbox checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} />}
              label="I agree to the terms and conditions"
            />
            <TextField
              fullWidth
              label="Type your full name"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Your full name"
              helperText="This name is recorded as your digital signature."
              autoComplete="name"
              required
            />
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<Check size={20} />}
                onClick={handleAccept}
                disabled={!agreedToTerms || !signerName.trim() || !additionalInfoComplete || submitting || isBlockedByParent}
              >
                {submitting ? <CircularProgress size={24} /> : 'Accept agreement'}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<X size={20} />}
                onClick={() => setShowDeclineForm(true)}
                disabled={submitting}
              >
                Decline
              </Button>
            </Box>
          </Box>
        )}

        {!isAccepted && showDeclineForm && (
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              label="Reason for declining (optional)"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <Box display="flex" gap={2}>
              <Button variant="contained" color="error" onClick={handleDecline} disabled={submitting}>
                {submitting ? <CircularProgress size={24} /> : 'Confirm Decline'}
              </Button>
              <Button variant="outlined" onClick={() => setShowDeclineForm(false)} disabled={submitting}>
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {isAccepted && proposal?.acceptance && (
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <AgreementAcceptedAdditionalInfo
              responses={proposal.acceptance.additionalInformationResponses}
            />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
              Acceptance record
            </Typography>
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 2.5,
                fontSize: '0.875rem',
                color: 'text.secondary',
                '& li': { mb: 0.5 }
              }}
            >
              <li>
                <strong>Accepted at:</strong>{' '}
                {format(new Date(proposal.acceptance.acceptedAt), "d MMMM yyyy 'at' HH:mm")} (UK)
              </li>
              <li>
                <strong>Contract version:</strong> {proposal.acceptance.contractVersion || '1.0'}
              </li>
              {proposal.acceptance.signerName && (
                <li>
                  <strong>Signed name:</strong> {proposal.acceptance.signerName}
                </li>
              )}
              {proposal.acceptance.acceptedBy && (
                <li>
                  <strong>Accepted by:</strong>{' '}
                  {[proposal.acceptance.acceptedBy.firstName, proposal.acceptance.acceptedBy.lastName]
                    .filter(Boolean)
                    .join(' ')}
                  {proposal.acceptance.acceptedBy.email && (
                    <> ({proposal.acceptance.acceptedBy.email})</>
                  )}
                </li>
              )}
              {proposal.acceptance.ipAddress && (
                <li>
                  <strong>IP address:</strong> {proposal.acceptance.ipAddress}
                </li>
              )}
              {proposal.acceptance.userAgent && (
                <li>
                  <strong>User agent:</strong>{' '}
                  <Box component="span" sx={{ wordBreak: 'break-all' }}>
                    {proposal.acceptance.userAgent}
                  </Box>
                </li>
              )}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default ProposalViewPage;
